<template>
    <v-dialog
        :value="isOpen"
        max-width="450px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Palety
                </h1>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <v-col cols="12" sm="8">
                        <v-text-field
                            ref="palletNumberField"
                            v-model="pallet"
                            clearable
                            label="Numer palety"
                            hint="Wpisz lub zeskanuj numer palety"
                            outlined
                            dense
                            :error-messages="palletErrors"
                            :disabled="isCheckingPallet"
                            autofocus
                            @input="$v.pallet.$touch()"
                        />
                    </v-col>
                    <v-col>
                        <v-btn
                            color="success"
                            data-sentry-label="Dodaj numer palety"
                            :loading="isCheckingPallet"
                            :disabled="$v.pallet.$invalid || isCheckingPallet"
                            @click="addPalletToList(pallet)"
                        >
                            <span> Dodaj </span>
                        </v-btn>
                    </v-col>
                </v-row>
                <v-data-table
                    :headers="headers"
                    :items="palletsList"
                    :mobile-breakpoint="0"
                    hide-default-footer
                >
                    <template #item.parameter="{item}">
                        {{ item.parameter }}
                    </template>
                    <template #item.details="{item}">
                        <v-tooltip :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn
                                    icon
                                    data-sentry-label="Usuń paletę"
                                    :disabled="lastPallet && !!reportId"
                                    v-on="on"
                                    @click="removePallet(item)"
                                >
                                    <v-icon color="pink">
                                        mdi-delete
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Usuń</span>
                        </v-tooltip>
                    </template>
                    <template slot="no-results">
                        <p class="text-center">
                            Brak danych
                        </p>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Zamknij
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler } from '@/utils/errorHandler'
import { required } from 'vuelidate/lib/validators'

export default {
    name: 'PalletsDialog',
    mixins: [
        pagination,
        dateFormatter,
        tooltipDelay,
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        palletsList: {
            type: Array,
            default: () => []
        },
        reportId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            headers: [
                {
                    text: 'Numer palety',
                    value: 'parameter',
                    sortable: false,
                    width: 300,
                },
                {
                    text: 'Akcje',
                    value: 'details',
                    sortable: false,
                    align: 'center'
                }
            ],
            pallet: null,
            isCheckingPallet: false,
        }
    },
    validations() {
        return {
            pallet: {
                required,
                isCorrectPallet: value => {
                    return /\.CF$/.test(value)
                }
            }
        }
    },
    computed: {
        allPallets() {
            return this.palletsList
        },
        lastPallet() {
            return this.allPallets.length === 1
        },
        palletErrors() {
            const errors = []

            if (!this.$v.pallet.$dirty) return errors
            !this.$v.pallet.required && errors.push('Kod palety jest wymagany.')
            !this.$v.pallet.isCorrectPallet && errors.push('Podany kod jest nieprawidłowy')

            return errors
        },
    },
    methods: {
        removePallet(item) {
            if (this.reportId) {
                this.$store.dispatch('reports/removePallet', item.id)
                    .then(() => {})
                    .catch(error => {
                        errorHandler(error)
                    })
            }
            const index = this.palletsList.indexOf(item)

            this.allPallets.splice(index, 1)
            this.$refs.palletNumberField.focus()
        },
        async addPalletToList(palletNumber) {
            const trimmedPallet = typeof palletNumber === 'string'
                ? palletNumber.trim()
                : ''

            this.pallet = trimmedPallet
            this.$v.pallet.$touch()

            if (!trimmedPallet || this.$v.pallet.$invalid) {
                this.$refs.palletNumberField.focus()

                return
            }

            const alreadyExistedPallet = this.allPallets.find(pallet => pallet.parameter === trimmedPallet)

            if (alreadyExistedPallet) {
                this.$store.dispatch('showNotification', {
                    type: 'primary',
                    message: 'Wprowadzony numer został już wcześniej dodany.'
                })
                this.pallet = null
                this.$refs.palletNumberField.focus()

                return
            }

            this.isCheckingPallet = true

            let parameterToSave = trimmedPallet

            try {
                const validationResult = await this.$store.dispatch('reports/checkPalletParameter', trimmedPallet)

                if (validationResult && validationResult.exists === false) {
                    this.$store.dispatch('showNotification', {
                        type: 'error',
                        message: 'Paleta o podanym numerze nie istnieje.'
                    })
                    this.pallet = null
                    this.isCheckingPallet = false
                    this.$refs.palletNumberField.focus()

                    return
                }

                if (validationResult && typeof validationResult.parameter === 'string') {
                    parameterToSave = validationResult.parameter.trim()
                }
            } catch (error) {
                this.isCheckingPallet = false
                this.$refs.palletNumberField.focus()

                return
            }

            if (this.allPallets.find(pallet => pallet.parameter === parameterToSave)) {
                this.$store.dispatch('showNotification', {
                    type: 'primary',
                    message: 'Wprowadzony numer został już wcześniej dodany.'
                })
                this.pallet = null
                this.isCheckingPallet = false
                this.$refs.palletNumberField.focus()

                return
            }

            if (!this.reportId) {
                this.allPallets.push({'parameter': parameterToSave})
                this.pallet = null
                this.isCheckingPallet = false
                this.$refs.palletNumberField.focus()

                return
            }

            this.$store.dispatch('reports/addPallet', {
                'reportId': this.reportId,
                'parameter': parameterToSave
            })
                .then(response => {
                    this.allPallets.push({'parameter': response.parameter, 'reportId': response.reportId, 'id': response.id})
                    this.pallet = null
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.isCheckingPallet = false
                    this.$refs.palletNumberField.focus()
                })
        },
        onClose() {
            this.$emit('on-close')

            setTimeout(() => {
                this.pallet = null
                this.$v.$reset()
            }, 500)
        }
    }
}
</script>
